/**
 * Account Not Found Page
 *
 * Displayed when a user authenticates via Auth0 but doesn't have
 * an account in the application database.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  Mail,
  ArrowLeft,
  HelpCircle,
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

interface LoginError {
  email: string
  errorCode: string
  message: string
  supportEmail?: string
}

export default function AccountNotFoundPage() {
  const navigate = useNavigate()
  const [errorInfo, setErrorInfo] = useState<LoginError | null>(null)

  useEffect(() => {
    // Get error info from session storage
    const storedError = sessionStorage.getItem('auth0_login_error')
    if (storedError) {
      try {
        setErrorInfo(JSON.parse(storedError))
      } catch {
        // Invalid JSON, ignore
      }
      // Clear the error so it doesn't persist on refresh
      sessionStorage.removeItem('auth0_login_error')
    }
  }, [])

  const handleBackToLogin = () => {
    navigate('/login')
  }

  const isInactive = errorInfo?.errorCode === 'USER_INACTIVE'

  return (
    <div className="min-h-screen bg-background flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className={cn(
            'rounded-full p-3',
            isInactive ? 'bg-yellow-100' : 'bg-red-100'
          )}>
            <AlertCircle size={48} className={cn(
              isInactive ? 'text-yellow-600' : 'text-red-600'
            )} />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground">
          {isInactive ? 'Account Deactivated' : 'Account Not Found'}
        </h2>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          {errorInfo?.email && (
            <span className="font-medium">{errorInfo.email}</span>
          )}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          <CardContent className="pt-8 pb-8 px-6 sm:px-10">
            <div className="text-center">
              <p className="text-foreground mb-6">
                {errorInfo?.message || (
                  isInactive
                    ? 'Your account has been deactivated. Please contact your administrator to restore access.'
                    : 'We could not find an account associated with your email address. Please contact your administrator to request access.'
                )}
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <HelpCircle size={20} className="text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-left text-sm text-blue-700">
                    <p className="font-medium mb-1">Need access?</p>
                    <p>
                      Contact your organisation administrator to have your account created or reactivated.
                      They will need your email address to set up your access.
                    </p>
                  </div>
                </div>
              </div>

              {errorInfo?.supportEmail && (
                <div className="mb-6">
                  <Button asChild>
                    <a
                      href={`mailto:${errorInfo.supportEmail}?subject=Access Request&body=Hi,%0D%0A%0D%0AI would like to request access to the Operational Resilience platform.%0D%0A%0D%0AEmail: ${errorInfo.email || ''}%0D%0A%0D%0AThank you.`}
                    >
                      <Mail size={16} className="mr-2" />
                      Contact Support
                    </a>
                  </Button>
                </div>
              )}

              <Button
                variant="outline"
                onClick={handleBackToLogin}
              >
                <ArrowLeft size={16} className="mr-2" />
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          If you believe this is an error, please contact your system administrator.
        </p>
      </div>
    </div>
  )
}
